import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes, FaExpand } from 'react-icons/fa';
import './ProjectGallery.css';
import { MobileScrollRail } from './MobileScrollRail';
import projectImages from '../utils/projectImages';
import { getProjectGalleryVideo, getVideoMimeType, isQuickTimeProjectVideo } from '../utils/projectVideos';

function ProjectGallery({ project, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const galleryRef = useRef(null);
  const thumbnailsRef = useRef(null);
  const videoRef = useRef(null);

  const slugKey = project.slug || null;
  const bundledImages = slugKey ? (projectImages[slugKey] || []) : [];
  const remoteImages = Array.isArray(project.images)
    ? project.images.filter(Boolean)
    : [];
  const images = bundledImages.length > 0 ? bundledImages : remoteImages;
  const videoSrc = getProjectGalleryVideo(slugKey);
  const videoType = videoSrc ? getVideoMimeType(videoSrc) : 'video/mp4';
  const useQuickTimeSources = slugKey ? isQuickTimeProjectVideo(slugKey) : false;
  const galleryOnly = !videoSrc;

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') setCurrentIndex((i) => Math.min(i + 1, images.length - 1));
    if (e.key === 'ArrowLeft') setCurrentIndex((i) => Math.max(i - 1, 0));
  }, [onClose, images.length]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyLeft = body.style.left;
    const prevBodyRight = body.style.right;
    const prevBodyWidth = body.style.width;

    html.style.overflow = 'hidden';
    html.style.setProperty('overscroll-behavior', 'none');
    body.style.overflow = 'hidden';
    body.style.setProperty('overscroll-behavior', 'none');
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      html.style.overflow = prevHtmlOverflow;
      html.style.removeProperty('overscroll-behavior');
      body.style.overflow = prevBodyOverflow;
      body.style.removeProperty('overscroll-behavior');
      body.style.position = prevBodyPosition;
      body.style.top = prevBodyTop;
      body.style.left = prevBodyLeft;
      body.style.right = prevBodyRight;
      body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (thumbnailsRef.current) {
      const thumb = thumbnailsRef.current.children[currentIndex];
      if (thumb) thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentIndex]);

  useEffect(() => {
    setVideoFailed(false);
    const v = videoRef.current;
    if (!v) return undefined;

    const tryPlay = () => {
      v.muted = true;
      v.defaultMuted = true;
      const p = v.play();
      if (p !== undefined) p.catch(() => {});
    };

    v.addEventListener('canplay', tryPlay);
    v.addEventListener('loadeddata', tryPlay);
    tryPlay();

    return () => {
      v.removeEventListener('canplay', tryPlay);
      v.removeEventListener('loadeddata', tryPlay);
      v.pause();
    };
  }, [videoSrc, slugKey]);

  const goNext = () => setCurrentIndex((i) => Math.min(i + 1, images.length - 1));
  const goPrev = () => setCurrentIndex((i) => Math.max(i - 1, 0));

  const toggleFullscreen = () => {
    setIsFullscreen((f) => !f);
  };

  if (!images.length) return null;

  return (
    <div className="pg-overlay" onClick={onClose}>
      <div className="pg-container" onClick={(e) => e.stopPropagation()}>
        <button className="pg-close" onClick={onClose} aria-label="Close gallery">
          <FaTimes />
        </button>

        <div className={`pg-layout${galleryOnly ? ' pg-layout--gallery-only' : ''}`}>
          {/* Left: Video (case-study projects with bundled video only) */}
          {videoSrc ? (
            <div className="pg-video-panel">
              {videoFailed && images[0] && (
                <img src={images[0]} alt="" className="pg-video-fallback" aria-hidden="true" />
              )}
              <video
                ref={videoRef}
                key={`${slugKey || project.title}-${videoSrc}`}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={images[0] || undefined}
                className={`pg-video ${videoFailed ? 'pg-video-hidden' : ''}`}
                onError={() => setVideoFailed(true)}
              >
                {useQuickTimeSources ? (
                  <>
                    <source src={videoSrc} type="video/quicktime" />
                    <source src={videoSrc} type="video/mp4" />
                  </>
                ) : (
                  <source src={videoSrc} type={videoType} />
                )}
              </video>
              <div className="pg-video-overlay">
                <h2 className="pg-title">{project.title}</h2>
                <p className="pg-location">{project.location}</p>
              </div>
            </div>
          ) : null}

          {/* Image viewer (+ title when no video) */}
          <div className="pg-gallery-panel">
            {galleryOnly ? (
              <div className="pg-gallery-head">
                <h2 className="pg-title">{project.title}</h2>
                {project.location ? <p className="pg-location">{project.location}</p> : null}
              </div>
            ) : null}
            <div className={`pg-main-image ${isFullscreen ? 'pg-fullscreen' : ''}`} ref={galleryRef}>
              <img
                src={images[currentIndex]}
                alt={`${project.title} - ${currentIndex + 1}`}
                className="pg-image"
              />

              <div className="pg-image-counter">
                {currentIndex + 1} / {images.length}
              </div>

              <button className="pg-fullscreen-btn" onClick={toggleFullscreen} aria-label="Toggle fullscreen">
                <FaExpand />
              </button>

              {currentIndex > 0 && (
                <button className="pg-arrow pg-arrow-left" onClick={goPrev} aria-label="Previous">
                  <FaChevronLeft />
                </button>
              )}
              {currentIndex < images.length - 1 && (
                <button className="pg-arrow pg-arrow-right" onClick={goNext} aria-label="Next">
                  <FaChevronRight />
                </button>
              )}
            </div>

            {/* Thumbnail Strip */}
            <div className="pg-thumbnails" ref={thumbnailsRef}>
              {images.map((img, i) => (
                <button
                  key={i}
                  className={`pg-thumb ${i === currentIndex ? 'pg-thumb-active' : ''}`}
                  onClick={() => setCurrentIndex(i)}
                >
                  <img src={img} alt={`Thumb ${i + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
            <MobileScrollRail scrollerRef={thumbnailsRef} />
          </div>
        </div>
      </div>

      {/* Fullscreen Image Overlay */}
      {isFullscreen && (
        <div className="pg-fs-overlay" onClick={toggleFullscreen}>
          <img src={images[currentIndex]} alt={project.title} className="pg-fs-image" />
          <button className="pg-fs-close" onClick={toggleFullscreen}><FaTimes /></button>
          {currentIndex > 0 && (
            <button className="pg-fs-arrow pg-fs-arrow-left" onClick={(e) => { e.stopPropagation(); goPrev(); }}>
              <FaChevronLeft />
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button className="pg-fs-arrow pg-fs-arrow-right" onClick={(e) => { e.stopPropagation(); goNext(); }}>
              <FaChevronRight />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectGallery;
